<template>
  <div class="attachable-category-paginated-overview" v-bind:class="{loaded: loaded}">

    <attachable-move-modal
        :attachables="moveAttachables"
        :customer-id="customerId"
        :category="category"
        :visible="showMoveModal"
        v-on:close="onCloseMoveModal"
        v-on:complete="onMoveModalComplete"
    ></attachable-move-modal>

    <attachable-copy-modal
        :attachables="copyAttachables"
        :customer-id="customerId"
        :category="category"
        :visible="showCopyModal"
        v-on:close="onCloseCopyModal"
        v-on:complete="onCopyModalComplete"
    ></attachable-copy-modal>

    <div style="padding: 0.5em;" class="options-container">
      <div v-if="hasItems || filterActive || filterInitialized" class="options-container">
        <laravel-pagination
            v-if="displayPagination"
            :data="currentRequestData"
            @pagination-change-page="loadPage"
        ></laravel-pagination>

        <div class="per-page">
          Per pagina
          <a-select v-model="perPage" @change="onPerPageChange" style="width: 70px;">
            <a-select-option :value="10">10</a-select-option>
            <a-select-option :value="15">15</a-select-option>
            <a-select-option :value="30">30</a-select-option>
            <a-select-option :value="60">60</a-select-option>
            <a-select-option :value="100">100</a-select-option>
          </a-select>
        </div>

        <attachable-overview-filter-component
            v-bind="$attrs"
            @filter-request="getFilteredAttachables($event)"
        />
      </div>

      <a-dropdown class="global-actions" :trigger="['click']" placement="bottomRight" v-on:click.stop>
        <a-button type="default" @click.stop="e => e.preventDefault()">
          Acties
          <template v-if="hasSelectedAttachables">
            ({{ selectedAttachablesCount }})
          </template>
          <a-icon type="down"/>
        </a-button>

        <a-menu slot="overlay">
          <a-menu-item key="copy" class="action-copy" :disabled="!hasSelection" @click="onCopyClick">
            <content-copy-icon></content-copy-icon>
            Kopieer naar
          </a-menu-item>
          <a-menu-item key="move" class="action-move" :disabled="!hasSelection" @click="onMoveClick">
            <content-cut-icon></content-cut-icon>
            Verplaats naar
          </a-menu-item>
          <a-menu-item key="download-multiple" class="action-download-multiple" :disabled="!hasSelection"
                       @click="onDownloadClick">
            <download-icon></download-icon>
            Downloaden
          </a-menu-item>
          <a-menu-divider/>
          <a-menu-item key="delete" class="action-delete" :disabled="!hasSelection" @click="onDeleteClick">
            <delete-icon></delete-icon>
            Verwijderen
          </a-menu-item>
        </a-menu>
      </a-dropdown>
    </div>

    <table class="table table-striped">
      <thead :class="{ loading: loading }">
      <tr v-if="hasItems && isLoaded">
        <th class="compact half-padding">
          <a-checkbox ref="globalCheckbox" :checked="allSelected" :indeterminate="indeterminate"
                      @change="onSelectAllChanged"></a-checkbox>
        </th>
        <th>
          <order-by value="child_name" :select="orderBy" :order="order" @change="onOrderByChange">
            Naam
          </order-by>
        </th>
        <th class="compact">
          <order-by value="child_created_at" :select="orderBy" :order="order" @change="onOrderByChange"
                    class="child-created-at">
            Aangemaakt op
          </order-by>
        </th>
        <th class="compact">
          <order-by value="child_file_size" :select="orderBy" :order="order" @change="onOrderByChange">
            Grootte
          </order-by>
        </th>
        <th class="compact"></th>
        <th class="compact"></th>
      </tr>
      <tr v-if="!hasItems && filterActive">
        <th class="compact">
          Er zijn geen documenten gevonden die voldoen aan de actieve filters.
        </th>
      </tr>
      </thead>
      <tbody v-bind:class="{ loading: loading }">
      <AttachableSelectableTableRowComponent
          v-for="attachable in attachables"
          :key="attachable.id"
          :attachable="attachable"
          :show-breadcrumbs="true"
          :publish-documents="true"
          @update="onUpdate"
          v-on:selection-changed="onSelectionChanged"
          v-on:copy="onCopy"
          v-on:move="onMove"
          v-on:download="onDownload"
          v-on:delete="onDelete"
      ></AttachableSelectableTableRowComponent>
      </tbody>
    </table>

    <div v-if="!hasItems && isLoaded && !filterActive && !loading" style="padding: 0 10px 15px;">
      Er staan nog geen documenten in deze categorie.
    </div>

    <div v-if="displayPagination" style="padding: 8px">
      <laravel-pagination
          v-if="displayPagination"
          :data="currentRequestData"
          @pagination-change-page="loadPage"
          style="margin: 0;"
      ></laravel-pagination>
    </div>

    <a style="display:none;" ref="downloadLink" download="" href=""></a>
    <form
        ref="zipForm"
        method="POST"
        action=""
        target="_blank"
        style="display: none;"
    >
      <div ref="zipIdsContainer"></div>
    </form>
  </div>
</template>

<script>
import attachableApi from "~/api/attachable";
import documentApi from "~/api/document";
import attachableCategoryApi from "~/api/attachable_category";
import attachableCategoryStore from "~/modules/attachable_category/store";
import { mapGetters } from "vuex";
import AttachableSelectableTableRowComponent from "~/modules/attachable/components/SelectableTableRowComponent";
import AttachableOverviewFilterComponent from "../../attachable/components/AttachableOverviewFilterComponent";

export default {
  name: "CategoryPaginatedAttachableOverviewComponent",
  props: {
    attachableCategory: {
      type: Object,
      default: null,
    },
    attachableCategoryId: {
      type: [String, Number],
      default: null,
    },
    customerId: {
      type: [String, Number],
      required: true,
    },
    category: {
      type: [String, Number],
      required: true,
    },
  },
  components: {
    AttachableOverviewFilterComponent,
    AttachableSelectableTableRowComponent
  },
  data() {
    return {
      previousSelectedAttachable: null,
      showMoveModal: false,
      showCopyModal: false,
      showUploadModal: false,
      attachableCategoryValue: null,
      reloadTimeout: null,
      attachables: [],
      moveAttachables: [],
      copyAttachables: [],
      currentPage: null,
      currentRequestData: null,
      allSelected: false,
      loading: true,
      loaded: false,
      page: 1,
      perPage: 15,
      orderBy: "child_created_at",
      order: "desc",
      filter: {},
    };
  },
  async created() {
    const STORE_KEY = "$_attachable_category";
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, attachableCategoryStore);
    }

    await this.$store.dispatch("fetchCustomer", this.customerId);

    this.initAttachables();
  },
  computed: {
    ...mapGetters('$_attachable_category', {
      selectedAttachables: 'selectedAttachables',
    }),
    customer() {
      return this.$store.getters.getCustomerById(this.customerId);
    },
    selectedAttachablesCount() {
      return this.selectedAttachables.length;
    },
    selectedAttachableIds() {
      return this.selectedAttachables.map(attachable => attachable.id);
    },
    hasSelectedAttachables() {
      return this.selectedAttachablesCount > 0;
    },
    indeterminate() {
      return this.allSelected === false
          && this.currentSelectionDiff !== this.attachables.length
          && this.currentSelectionDiff > 0;
    },
    currentSelectionDiff() {
      return this.attachables.filter(i => this.selectedAttachables.findIndex(x => x.id === i.id) < 0).length;
    },
    displayPagination() {
      return this.isLoaded && this.hasItems && this.hasPages && typeof this.currentRequestData.meta !== "undefined";
    },
    hasSelection() {
      return this.selectedAttachables.length > 0;
    },
    isLoaded() {
      return this.currentRequestData !== null;
    },
    hasItems() {
      return this.currentTotalItemCount !== null && this.currentTotalItemCount > 0;
    },
    hasPages() {
      if (this.isLoaded) {
        return this.currentRequestData.meta.pagination.total_pages > 1;
      }
      return false;
    },
    currentTotalItemCount() {
      return (
          this.currentRequestData !== null
          && typeof this.currentRequestData.meta !== "undefined"
          && this.currentRequestData.meta !== null
          && typeof this.currentRequestData.meta.pagination !== "undefined"
          && this.currentRequestData.meta.pagination !== null
          && typeof this.currentRequestData.meta.pagination.total !== "undefined"
          && this.currentRequestData.meta.pagination.total !== null
      )
          ? this.currentRequestData.meta.pagination.total
          : null;
    },
    filterInitialized() {
      return Object.keys(this.filter).length > 0;
    },
    filterActive() {
      if (Object.entries(this.filter)) {
        for (const [key, value] of Object.entries(this.filter)) {
          if (value.length > 0) return true;
        }
      }
      return false;
    }
  },
  methods: {
    initAttachables() {
      this.loadPage(this.page, true);
    },
    loadPage(page = 1, forceReload = false, emitEvents = true) {
      if (page === this.currentPage && forceReload !== true) {
        return;
      }
      this.loading = true;

      attachableApi.getByCustomerIdAndCategory(
          this.customerId,
          this.category,
          page,
          this.perPage,
          this.orderBy,
          this.order,
          ["child_instance", "child_instance.breadcrumbs", "parent_instance", "child_instance.product_labels"],
          this.filter,
      )
          .then((response) => {
            this.loaded = true;
            this.allSelected = false;
            this.attachables = response.data;
            this.currentRequestData = response;
            this.loading = false;
            this.currentPage = page;
            if (emitEvents) {
              this.$emit("total-updated", this.category, this.currentRequestData.meta.pagination.total);
            }
          })
          .catch(() => {
            this.$notification["error"]({
              message: "Er ging iets mis",
              description: "Het is niet gelukt om documenten te laden",
            });
          });
    },
    onSave() {
      this.saving = true;
      this.document.name = this.name;

      documentApi.update(this.document)
          .then(() => {
            this.saving = false;
            this.editing = false;
          })
          .catch(() => {
            this.saving = false;
            this.$notification["error"]({
              message: "Er ging iets mis",
              description: "Het is niet gelukt om de naam van het document te wijzigen",
            });
          });
    },
    onSelectionChanged(attachable, selected, shiftKey) {
      if (this.loading) {
        return;
      }

      if (selected === true) {
        this.selectAttachable(attachable);
      } else {
        this.deselectAttachable(attachable);
      }
    },
    onSelectAllChanged() {
      this.allSelected = !this.allSelected;

      for (let i in this.attachables) {
        let attachable = this.attachables[i];

        if (this.allSelected === true && this.selectedAttachables.findIndex(x => x.id === attachable.id) < 0) {
          this.selectAttachable(attachable);
        } else if (this.allSelected === false) {
          this.deselectAttachable(attachable);
        }
      }
    },
    deselectAll() {
      this.$store.dispatch("$_attachable_category/resetSelectedAttachables");
    },
    selectAttachable(attachable, shiftKey = false) {
      this.selectedAttachables.push(attachable);

      if (this.currentSelectionDiff === 0) {
        this.allSelected = true;
      }

      if (shiftKey) {
        // TODO: add all attachables to the current selection which are in between the current and previous selected attachable
      }

      this.previousSelectedAttachable = attachable;
    },
    deselectAttachable(attachable, shiftKey = false) {
      this.allSelected = false;

      this.selectedAttachables.splice(this.selectedAttachables.findIndex(x => x.id === attachable.id), 1);

      if (shiftKey) {
        // TODO: remove all attachables from the current selection which are in between the current and previous selected attachable
      }

      this.previousSelectedAttachable = attachable;
    },
    onPerPageChange(value) {
      if (this.currentTotalItemCount !== null) {
        let newTotalPages = Math.ceil(this.currentTotalItemCount / value);

        if (this.currentPage > newTotalPages) {
          this.page = 1;
        } else {
          this.page = this.currentPage;
        }
      } else {
        this.page = 1;
      }

      this.reloadPage();
    },
    onOrderByChange(orderBy, order) {
      this.orderBy = orderBy;
      this.order = order;
      this.loadPage(this.currentPage, true);
    },
    onCopyClick() {
      if (this.hasSelection) {
        this.copyAttachables = this.selectedAttachables;
        this.showCopyModal = true;
      }
    },
    onMoveClick() {
      if (this.hasSelection) {
        this.moveAttachables = this.selectedAttachables;
        this.showMoveModal = true;
      }
    },
    onDownloadClick() {
      attachableApi
          .generateDownloadZipLink(this.selectedAttachableIds)
          .then(response => {
            this.$refs.zipForm.action = response.data.uri;
            // Empty the container
            this.$refs.zipIdsContainer.innerHTML = "";

            // Add hidden input for each id
            this.selectedAttachableIds.forEach(id => {
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = "ids[]";
              input.value = id;
              this.$refs.zipIdsContainer.appendChild(input);
            });

            this.$refs.zipForm.submit();
          });
    },
    onDeleteClick() {
      if (this.hasSelection) {
        let $self = this,
            selectedAttachableIds = this.selectedAttachableIds;
        this.$confirm({
          title: this.selectedAttachablesCount > 1
              ? "Weet je zeker dat je deze documenten " + this.selectedAttachablesCount + " wil verwijderen?"
              : "Weet je zeker dat je dit document wil verwijderen",
          content: "De geselecteerde documenten zullen worden verwijderd. Deze actie is niet terug te draaien",
          okText: "Verwijderen",
          okType: "danger",
          cancelText: "Annuleren",
          onOk() {
            return attachableApi.deleteAll(selectedAttachableIds)
                .then(response => {
                  $self.deselectAll();
                  $self.reloadPage();
                });
          },
          onCancel() {
          },
        });
      }
    },
    onCopy(attachable) {
      this.copyAttachables = [attachable];
      this.showCopyModal = true;
    },
    onCloseCopyModal() {
      this.showCopyModal = false;
    },
    onCopyModalComplete() {
      this.copyAttachables = [];
      this.deselectAll();
      this.page = 1;
      this.reloadPage();
    },
    onMove(attachable) {
      this.moveAttachables = [attachable];
      this.showMoveModal = true;
    },
    onCloseMoveModal() {
      this.showMoveModal = false;
    },
    reloadPage(page = null) {
      if (page === null) {
        this.page = this.currentPage;
      } else {
        this.page = page;
      }

      if (this.reloadTimeout !== null) {
        clearTimeout(this.reloadTimeout);
      }

      this.reloadTimeout = setTimeout(() => {
        this.loadPage(this.page, true);
      }, 200);
    },
    onMoveModalComplete() {
      this.moveAttachables = [];
      this.deselectAll();
      this.page = 1;
      this.reloadPage();
    },
    onUpdate(attachable) {
      attachableApi.update(attachable)
          .then()
          .catch(error => {
            this.$notification["error"]({ message: "De wijziging kon niet worden opgeslagen." });
          });
    },
    onDelete(attachable) {
      let $self = this;

      this.$confirm({
        title: "Weet je zeker dat je dit documenten wil verwijderen?",
        content: "Het geselecteerde document zal worden verwijderd. Deze actie is niet terug te draaien",
        okText: "Verwijderen",
        okType: "danger",
        cancelText: "Annuleren",
        onOk() {
          return attachableApi.delete(attachable.id)
              .then(response => {
                $self.reloadPage();
              });
        },
        onCancel() {
        },
      });
    },
    onDownload(attachable) {
      attachableApi.find(attachable.id, ["child_instance", "child_instance.content"])
          .then(response => {
            let attachable = response.data,
                isAttachable = attachable.child_instance.data,
                content = isAttachable.content.data.content,
                filename = isAttachable.name,
                extensionRegex = new RegExp(isAttachable.extension + "$");

            if (!extensionRegex.test(filename)) {
              filename += isAttachable.extension;
            }

            this.$refs.downloadLink.href = window.URL.createObjectURL(
                new Blob([Buffer.from(content, "base64")], { type: isAttachable.mime_type })
            );
            this.$refs.downloadLink.download = filename;
            this.$refs.downloadLink.click();
          });
    },
    getFilteredAttachables(params) {
      this.filter = params;
      this.loadPage(this.page, true);
    }
  },
};
</script>

<style scoped>

.attachable-category-paginated-overview {
  min-height: 50px;
}

.attachable-category-paginated-overview .table {
  margin-bottom: 0;
}

tr th.half-padding {
  padding: 4px;
}

tr.selected {
  background-color: #1890ff1c;
}

table th.compact, table td.compact {
  white-space: nowrap;
  width: 1px !important;
}

table > tbody.loading {
  opacity: 0.5;
}

.loading {
  opacity: 0.5;
}

.options-container {
  display: flex;
  gap: 1em;
  justify-content: space-between;
  flex-direction: row;
  align-items: flex-start;
}

</style>
